import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { t } from 'ttag'
import { LineSegment, VictoryArea, VictoryAxis, VictoryChart, VictoryLine, createContainer } from 'victory'
import VictoryTheme from '../../styles/victory-theme'
import { IGlobalState } from '../../Root/store/appReducer'
import { Route, Units } from '../../services/api.types'
import { tokens } from '../../styles/tokens'
import { useOsScale } from '../../common/helpers/useOsScale'
import ProgressChips, { ProgressChipOption } from '../../views/Progress/components/ProgressChips'
import {
  RouteSeries,
  RouteStatKey,
  TempoKey,
  buildSeries,
  detectAvailableSeries,
  fillMissingSpeeds,
  formatElapsed,
  formatSeriesValue,
  normalizeSeries,
  valueAtElapsed,
} from './routeSeries'

// Voronoi (snap-to-nearest-time tooltips) combined with a cursor (vertical crosshair line that
// follows the pointer, making it easy to compare the series at one moment).
const VictoryCursorVoronoiContainer: any = createContainer('voronoi', 'cursor')

// Power and temperature have no fitting tokens.ts colors; violet and amber are distinct from the
// heart-rate red, tempo blue, and altitude gray.
const SERIES_COLOR: Record<RouteStatKey, string> = {
  hr: tokens.status.error,
  pace: tokens.brand.blue,
  spd: tokens.brand.blue,
  pwr: '#8B5CF6',
  tmp: '#F59E0B',
  alt: tokens.border.default,
}

// Altitude renders as a gray filled area along the chart bottom (like the elevation profile in
// Strava/Garmin charts), confined to the lower band; the left axis extrapolates its scale over
// the full chart height, which is what keeps the area visually low.
const ALT_FILL = '#D8DBE4'
const ALT_BAND = 0.35

// Same gridline color the Progress chart uses (victory-theme's blueGrey50).
const GRID_COLOR = 'rgb(236, 239, 241)'

const seriesLabel = (key: RouteStatKey): string => {
  switch (key) {
    case 'hr':
      return t`Heart rate`
    case 'pace':
      return t`Pace`
    case 'spd':
      return t`Speed`
    case 'pwr':
      return t`Power`
    case 'tmp':
      return t`Temperature`
    default:
      return t`Altitude`
  }
}

const useStyles = makeStyles({
  chipLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  // Colored dot in each chip doubling as the line legend.
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  // Anchors the HTML tooltip that slides along the top of the chart.
  chartWrapper: {
    position: 'relative',
    // Victory hard-defaults touch-action: none as an INLINE style on its container <div>
    // (class "VictoryContainer" — not the svg), which makes the chart swallow vertical swipes
    // on mobile. pan-y (!important to beat the inline style) lets the browser keep scrolling
    // the page vertically while horizontal drags still drive the cursor/tooltip.
    '& .VictoryContainer, & .VictoryContainer svg': {
      touchAction: 'pan-y !important',
    },
  },
  tooltip: {
    position: 'absolute',
    top: '4px',
    transform: 'translateX(-50%)',
    backgroundColor: '#fff',
    border: `1px solid ${tokens.border.default}`,
    borderRadius: '4px',
    padding: '6px 10px',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
    color: tokens.text.primary,
    lineHeight: 1.45,
    zIndex: 2,
  },
  tooltipTime: {
    fontWeight: 700,
  },
})

interface RouteStatsChartProps {
  routes: Route[]
  /** How to chart the speed samples: min/km-style pace (default) or km/h speed (cycling). */
  tempo?: TempoKey
  /**
   * Fires with the hovered elapsed workout seconds (null when the pointer leaves the chart).
   * Lets the parent highlight the matching position on the route map.
   */
  onHoverElapsed?: (elapsedSeconds: number | null) => void
}

interface ChartPoint {
  x: number
  y: number | null
  /** Real converted value for tooltips (y is normalized). */
  real: number | null
  seriesKey: RouteStatKey
}

/**
 * Time-series chart for an activity's route data. Altitude is always drawn as a gray area along
 * the bottom with its real scale on the left axis; heart rate and pace are toggleable lines —
 * pace plots inverted (faster is higher) with its scale on the right axis, heart rate lives in
 * the tooltips. Renders nothing when the route carries no chartable series. Visibility is
 * controlled by the parent (the stats row's "Show more" toggle).
 */
export default function RouteStatsChart({ routes, tempo = 'pace', onHoverElapsed }: RouteStatsChartProps) {
  const withSpeeds = useMemo(() => fillMissingSpeeds(routes), [routes])
  const available = useMemo(() => detectAvailableSeries(withSpeeds, tempo), [withSpeeds, tempo])
  if (!available.length) return null
  return (
    <RouteStatsChartInner routes={withSpeeds} available={available} tempo={tempo} onHoverElapsed={onHoverElapsed} />
  )
}

function RouteStatsChartInner({
  routes,
  available,
  tempo,
  onHoverElapsed,
}: {
  routes: Route[]
  available: RouteStatKey[]
  tempo: TempoKey
  onHoverElapsed?: (elapsedSeconds: number | null) => void
}) {
  const classes = useStyles()
  const osScale = useOsScale()
  const userUnits = useSelector((state: IGlobalState) => state.auth.userDto.units || Units.METRIC)

  // Altitude is the permanent backdrop; only the line series toggle. Heart rate and the tempo
  // series start enabled — power/temperature stay opt-in so the default chart stays readable
  // (falling back to everything when neither default series exists).
  const togglable = useMemo(() => available.filter((key) => key !== 'alt'), [available])
  const defaultVisible = useMemo(() => {
    const preferred = togglable.filter((key) => key === 'hr' || key === tempo)
    return preferred.length ? preferred : togglable
  }, [togglable, tempo])
  const [visible, setVisible] = useState<RouteStatKey[]>(defaultVisible)

  // The hovered elapsed time drives both the local HTML tooltip and the map's position marker.
  const [hoveredElapsed, setHoveredElapsed] = useState<number | null>(null)
  const setHover = (elapsedSeconds: number | null) => {
    setHoveredElapsed(elapsedSeconds)
    onHoverElapsed?.(elapsedSeconds)
  }

  // Clear any lingering map highlight when the chart collapses mid-hover.
  useEffect(() => () => onHoverElapsed?.(null), [])
  const toggleSeries = (key: RouteStatKey) => {
    setVisible((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : togglable.filter((k) => prev.includes(k) || k === key),
    )
  }

  // Render the SVG at the container's real pixel width so text and line weights stay a constant
  // physical size everywhere. A fixed viewBox width would scale everything up on a wide desktop
  // card and down on a narrow phone card.
  const containerRef = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState<number | null>(null)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setChartWidth(el.clientWidth || null)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const seriesByKey = useMemo(() => {
    const map = {} as Record<RouteStatKey, RouteSeries>
    available.forEach((key) => {
      map[key] = buildSeries(routes, key, userUnits)
    })
    return map
  }, [routes, available, userUnits])

  const altSeries = available.includes('alt') ? seriesByKey.alt : null
  const tempoSeries = visible.includes(tempo) ? seriesByKey[tempo] : null
  const totalSeconds = seriesByKey[available[0]].totalSeconds

  const withReal = (display: { x: number; y: number | null }[], series: RouteSeries, key: RouteStatKey): ChartPoint[] =>
    // normalizeSeries preserves indexes, so the real value pairs up positionally.
    display.map((point, i) => ({ ...point, real: series.points[i].y, seriesKey: key }))

  const altPoints = useMemo(
    () => (altSeries ? withReal(normalizeSeries(altSeries, ALT_BAND), altSeries, 'alt') : null),
    [altSeries],
  )
  const linePoints: { key: RouteStatKey; points: ChartPoint[] }[] = useMemo(
    () =>
      visible.map((key) => {
        const series = seriesByKey[key]
        // Pace is inverted so faster (a smaller min/km value) plots higher, as in fitness apps.
        return { key, points: withReal(normalizeSeries(series, 1, key === 'pace'), series, key) }
      }),
    [visible, seriesByKey],
  )

  const chipOptions: ProgressChipOption[] = togglable.map((key) => ({
    value: key,
    label: (
      <span className={classes.chipLabel}>
        <span className={classes.dot} style={{ backgroundColor: SERIES_COLOR[key] }} />
        {seriesLabel(key)}
      </span>
    ),
  }))

  // True pixel sizes — the SVG renders at container width, so these aren't scaled further.
  const chartFontSize = 11 * osScale
  const tooltipFontSize = 10 * osScale

  // The tooltip is a plain HTML box pinned to the top of the chart, following the crosshair only
  // horizontally: one row per drawn series (bold time row first). Rendering it outside the SVG
  // gives full CSS control over rows and keeps its vertical position truly fixed.
  const tooltipKeys: RouteStatKey[] = [...visible, ...(altSeries ? (['alt'] as RouteStatKey[]) : [])]
  const tooltipRows = (x: number): string[] =>
    tooltipKeys.map((key) => {
      const value = valueAtElapsed(seriesByKey[key], x)
      return `${seriesLabel(key)}: ${value === null ? '-' : `${formatSeriesValue(key, value)} ${seriesByKey[key].yUnit}`}`
    })
  const chartHeight = chartWidth ? Math.min(280, Math.max(170, Math.round(chartWidth * 0.42))) : 0
  const axisPositions = [0, 0.25, 0.5, 0.75, 1]
  const paddingLeft = altSeries ? 52 : 16
  const paddingRight = tempoSeries ? 58 : 16

  // Approximate pixel x of the hovered time inside the plot area, used to slide the HTML tooltip
  // along the top of the chart (clamped so the box never leaves the container).
  const xMax = totalSeconds || 1
  const tooltipLeft = (x: number): number => {
    if (!chartWidth) return 0
    const plotWidth = chartWidth - paddingLeft - paddingRight
    const px = paddingLeft + (x / xMax) * plotWidth
    const half = 70
    return Math.min(Math.max(px, paddingLeft + half), chartWidth - half)
  }

  // Left axis: altitude's real scale, extrapolated over the full height from the bottom band the
  // area occupies (tick at normalized position p corresponds to min + (p / ALT_BAND) * range).
  // Units are appended to the tick numbers instead of a separate rotated axis label.
  const altTick = (p: number): string => {
    if (!altSeries) return ''
    return `${Math.round(altSeries.min + (p / ALT_BAND) * (altSeries.max - altSeries.min))} ${altSeries.yUnit}`
  }
  // Right axis: pace's real scale, mirrored because the pace line plots inverted.
  const tempoTick = (p: number): string => {
    if (!tempoSeries) return ''
    // Pace plots inverted (fastest at the top), so its axis is mirrored; speed plots normally.
    if (tempo === 'pace') {
      return `${formatSeriesValue('pace', tempoSeries.max - p * (tempoSeries.max - tempoSeries.min))}${tempoSeries.yUnit}`
    }
    return `${Math.round(tempoSeries.min + p * (tempoSeries.max - tempoSeries.min))} ${tempoSeries.yUnit}`
  }

  return (
    <div>
      {togglable.length > 0 && (
        <ProgressChips
          multiSelect
          options={chipOptions}
          values={visible}
          onChange={toggleSeries}
          ariaLabel={t`Chart data type`}
        />
      )}
      <div
        ref={containerRef}
        className={classes.chartWrapper}
        onMouseLeave={() => setHover(null)}
        onTouchEnd={() => setHover(null)}
        onTouchCancel={() => setHover(null)}
      >
        {hoveredElapsed !== null && chartWidth && (
          <div className={classes.tooltip} style={{ left: tooltipLeft(hoveredElapsed), fontSize: tooltipFontSize + 1 }}>
            <div className={classes.tooltipTime}>{formatElapsed(hoveredElapsed, totalSeconds)}</div>
            {tooltipRows(hoveredElapsed).map((row, index) => (
              <div key={index}>{row}</div>
            ))}
          </div>
        )}
        {chartWidth && (
          <VictoryChart
            theme={VictoryTheme}
            width={chartWidth}
            height={chartHeight}
            padding={{ top: 12, bottom: 32, left: paddingLeft, right: paddingRight }}
            domain={{ y: [0, 1.02] }}
            containerComponent={
              <VictoryCursorVoronoiContainer
                voronoiDimension="x"
                cursorDimension="x"
                cursorComponent={<LineSegment style={{ stroke: tokens.border.default, strokeWidth: 1 }} />}
                onActivated={(points: any[]) => {
                  if (points?.length) setHover(points[0].x)
                }}
              />
            }
          >
            <VictoryAxis
              tickFormat={(x: number) => formatElapsed(x, totalSeconds)}
              tickCount={5}
              style={{
                tickLabels: { fontSize: chartFontSize },
                grid: { stroke: GRID_COLOR, strokeWidth: 1 },
              }}
            />
            {altSeries && (
              <VictoryAxis
                dependentAxis
                tickValues={axisPositions}
                tickFormat={altTick}
                style={{
                  tickLabels: { fontSize: chartFontSize, fill: SERIES_COLOR.alt },
                  axis: { stroke: 'none' },
                  ticks: { stroke: 'none' },
                  grid: { stroke: GRID_COLOR, strokeWidth: 1 },
                }}
              />
            )}
            {tempoSeries && (
              <VictoryAxis
                dependentAxis
                orientation="right"
                tickValues={axisPositions}
                tickFormat={tempoTick}
                style={{
                  tickLabels: { fontSize: chartFontSize, fill: SERIES_COLOR[tempo] },
                  axis: { stroke: 'none' },
                  ticks: { stroke: 'none' },
                  grid: { stroke: 'none' },
                }}
              />
            )}
            {/* Altitude area first so it sits behind the lines. */}
            {altPoints && (
              <VictoryArea
                data={altPoints}
                interpolation="monotoneX"
                style={{ data: { fill: ALT_FILL, stroke: SERIES_COLOR.alt, strokeWidth: 1 } }}
              />
            )}
            {linePoints.map(({ key, points }) => (
              <VictoryLine
                key={key}
                data={points}
                interpolation="monotoneX"
                style={{ data: { stroke: SERIES_COLOR[key], strokeWidth: 1.5 } }}
              />
            ))}
          </VictoryChart>
        )}
      </div>
    </div>
  )
}
